import { useCallback, useRef, useState } from 'react';
import { Counter } from '../Counter';
import { useClickOutside } from '../../hooks/useClickOutside';
import {
  CounterLabel,
  CounterRow,
  Dropdown,
  TriggerButton,
  TriggerLabel,
  TriggerValue,
  Wrapper,
} from './GuestPicker.styles';
import type { GuestPickerProps } from './GuestPicker.types';

export function GuestPicker({
  adults,
  childCount,
  onAdultsChange,
  onChildrenChange,
  minAdults = 1,
  maxAdults = 20,
  minChildren = 0,
  maxChildren = 10,
  showChildren = true,
  renderInline = false,
  showShadow = false,
  labels: l,
}: GuestPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handleClose = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, handleClose, !renderInline);

  const defaultSummary = (a: number, c: number) =>
    `${a} Adult${a !== 1 ? 's' : ''}, ${c} Child${c !== 1 ? 'ren' : ''}`;

  const summary = (l?.summary ?? defaultSummary)(adults, childCount);
  const adultsLabel = l?.adults ?? 'Adults';
  const childrenLabel = l?.children ?? 'Children';
  const guestsLabel = l?.guests ?? 'Guests';

  const countersPanel = (
    <>
      <CounterRow>
        <CounterLabel>{adultsLabel}</CounterLabel>
        <Counter
          value={adults}
          min={minAdults}
          max={maxAdults}
          onChange={onAdultsChange}
          decreaseLabel={l?.decreaseAdults}
          increaseLabel={l?.increaseAdults}
        />
      </CounterRow>

      {showChildren && (
        <CounterRow>
          <CounterLabel>{childrenLabel}</CounterLabel>
          <Counter
            value={childCount}
            min={minChildren}
            max={maxChildren}
            onChange={onChildrenChange}
            decreaseLabel={l?.decreaseChildren}
            increaseLabel={l?.increaseChildren}
          />
        </CounterRow>
      )}
    </>
  );

  if (renderInline) {
    return (
      <Dropdown $asInline $withShadow={showShadow}>
        {countersPanel}
      </Dropdown>
    );
  }

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton onClick={() => setIsOpen(!isOpen)} type="button">
        <TriggerLabel>{guestsLabel}</TriggerLabel>
        <TriggerValue>{summary}</TriggerValue>
      </TriggerButton>

      {isOpen && <Dropdown>{countersPanel}</Dropdown>}
    </Wrapper>
  );
}
